/**
 * The invoice editor's four steps (BLOCK-1-REDESIGN §A.2, R-5) and where each field and each
 * validator issue belongs. The e2e helper `STEP_OF` (e2e/block1/ui.ts) mirrors this mapping.
 */
export type StepId = 'buyer' | 'reference' | 'details' | 'lines' | 'review';

/** The editor has two shapes: an invoice (step 1 is the buyer) and a Block 11 note (step 1 is the
 *  referenced invoice; the buyer is read-only, taken from the reference). */
export type EditorMode = 'invoice' | 'note';

/** In order: the currency (step 2) is chosen before the unit prices (step 3). */
export const STEPS: readonly StepId[] = ['buyer', 'details', 'lines', 'review'];

/** A note's steps: the reference replaces the buyer; the rest are the invoice steps in note mode. */
export const NOTE_STEPS: readonly StepId[] = ['reference', 'details', 'lines', 'review'];

/** The steps of the editor in the given mode. */
export function stepsFor(mode: EditorMode): readonly StepId[] {
  return mode === 'note' ? NOTE_STEPS : STEPS;
}

/**
 * The step titles, note-aware. The strings come from the caller's dictionary — Slice 4 adds the
 * note labels (`reference`, …); until then a note's reference step falls back to the buyer label,
 * which is never shown because no note editor renders yet. Step 1's title differs by mode (the
 * buyer vs the reference); the rest share the invoice titles.
 */
export interface StepTitleLabels {
  buyer: string;
  details: string;
  lines: string;
  review: string;
  reference?: string;
}

export function stepTitlesOf(labels: StepTitleLabels): Record<StepId, string> {
  return {
    buyer: labels.buyer,
    reference: labels.reference ?? labels.buyer,
    details: labels.details,
    lines: labels.lines,
    review: labels.review,
  };
}

/** Fields of step 2, by the first segment of their data-field name or issue path. */
const DETAILS_ROOTS: ReadonlySet<string> = new Set([
  'issueDate',
  'dateTimeIssued',
  'dueDate',
  'serviceDeliveryDate',
  'currency',
  'exchangeRate',
  'activityCode',
  'branchId',
  'purchaseOrderRef',
  'purchaseOrderDescription',
  'salesOrderRef',
  'salesOrderDescription',
  'proformaInvoiceNumber',
]);

/**
 * The step of a field (data-field name) or of a validator issue path. Anything without a step of
 * its own (e.g. `organization.rin`, the mock answer) belongs to the review.
 */
export function stepOfField(path: string): StepId {
  const root = path.split('.')[0] ?? '';
  if (root === 'receiver' || root === 'receiver-search' || root === 'kind') return 'buyer';
  // Block 11: a note's reference step (step 1). `referencedLineId` is the note's per-line link to
  // a referenced line; its top-level issues belong with the reference, not the lines.
  if (root === 'references' || root === 'referencedLineId') return 'reference';
  // `totalAmount:exceeds_reference` (the credit cap, notes-editor.md) is the lines step, not the
  // review — only a credit note ever carries a `totalAmount` issue (creditCapIssue).
  if (
    root === 'lines' ||
    root === 'add-items' ||
    root === 'extraDiscountAmount' ||
    root === 'totalAmount'
  )
    return 'lines';
  if (DETAILS_ROOTS.has(root)) return 'details';
  return 'review';
}

/** Open issues per step. */
export function issueCounts(issues: readonly { path: string }[]): Record<StepId, number> {
  const counts: Record<StepId, number> = {
    buyer: 0,
    reference: 0,
    details: 0,
    lines: 0,
    review: 0,
  };
  for (const issue of issues) counts[stepOfField(issue.path)] += 1;
  return counts;
}

/**
 * What the step list shows per step: each step its own open issues, and the review step the
 * document's total, so the last step never shows a check while an earlier one holds an issue
 * (RI-09).
 */
export function stepListCounts(counts: Record<StepId, number>): Record<StepId, number> {
  const review = Object.values(counts).reduce((total, n) => total + n, 0);
  return { ...counts, review };
}

/**
 * Where a reopened draft opens: the first step, in order, that holds a blocking issue of the
 * draft's stored readiness; the review when there is none (or only issues without a step).
 */
export function entryStep(issues: readonly { path: string }[] | null | undefined): StepId {
  const counts = issueCounts(issues ?? []);
  return STEPS.find((step) => step !== 'review' && counts[step] > 0) ?? 'review';
}

/** The data-field name that an issue's fix link moves focus to. */
export function fieldOfIssue(path: string): string {
  if (path.startsWith('receiver')) return 'receiver';
  // Block 11: a reference issue (`references` or `references.N:<key>`) is fixed at step 1's add
  // field (`ReferenceStep`, data-field "add-reference"), the counterpart of the lines add field.
  if (path === 'references' || path.startsWith('references.')) return 'add-reference';
  if (path === 'lines') return 'add-items';
  if (path === 'dateTimeIssued') return 'issueDate';
  const line = /^(lines\.\d+\.\w+)/.exec(path)?.[1];
  if (line)
    return line
      .replace(/\.(taxes|fixedTaxAmounts)$/, '.taxIds')
      .replace(/\.amountSold$/, '.unitPrice');
  return path;
}

export function nextStep(step: StepId): StepId | null {
  return STEPS[STEPS.indexOf(step) + 1] ?? null;
}

export function previousStep(step: StepId): StepId | null {
  const index = STEPS.indexOf(step);
  return index > 0 ? (STEPS[index - 1] ?? null) : null;
}
